import { z } from 'zod';
import type { IsoDate } from './day';
import { extensionStepMinutesSchema } from './reservation';

// Whole-minute clock positions in a day; 11:00 is 660. See requirements §9.
export const MINUTES_PER_HOUR = 60;

export const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

export const holdConfigSchema = z.object({
  includedHour: z.number().int().min(0).max(23),
  latestHour: z.number().int().min(0).max(23),
  stepMinutes: extensionStepMinutesSchema,
});

export type HoldConfig = z.infer<typeof holdConfigSchema>;

export type HoldChoice = { readonly minutes: number; readonly feeCents: number };

export function hourMinutes(hour: number): number {
  return hour * MINUTES_PER_HOUR;
}

// A selection sums its zones: two front-row spots cost twice one of them.
export function holdRateCents(perHourCents: readonly number[]): number {
  return perHourCents.reduce((total, rate) => total + rate, 0);
}

// Holding starts at the included hour or the clock, whichever is later. §9
export function holdFromMinutes(config: HoldConfig, nowMinutes: number | null): number {
  const included = hourMinutes(config.includedHour);
  return nowMinutes === null ? included : Math.max(included, nowMinutes);
}

// Whole steps to reach the deadline, rounded up; never a part of one. §9
export function holdFeeCents(
  perHourCents: readonly number[],
  minutes: number,
  config: HoldConfig,
  nowMinutes: number | null = null,
): number {
  const held = Math.max(0, minutes - holdFromMinutes(config, nowMinutes));
  const steps = Math.ceil(held / config.stepMinutes);
  return Math.round((steps * config.stepMinutes * holdRateCents(perHourCents)) / MINUTES_PER_HOUR);
}

// An extension buys the time between the two deadlines, and nothing before. §9
export function holdExtensionCents(
  perHourCents: readonly number[],
  fromMinutes: number,
  toMinutes: number,
): number {
  const added = Math.max(0, toMinutes - fromMinutes);
  return Math.round((added * holdRateCents(perHourCents)) / MINUTES_PER_HOUR);
}

// Every deadline the venue's own step allows, whatever the clock says. §9
export function holdSlots(config: HoldConfig): number[] {
  const slots: number[] = [];
  const last = hourMinutes(config.latestHour);
  for (
    let minutes = hourMinutes(config.includedHour);
    minutes <= last;
    minutes += config.stepMinutes
  )
    slots.push(minutes);
  return slots;
}

// The venue's own clock as a given day reads it: null ahead of it, spent behind.
export function clockOn(day: IsoDate, today: IsoDate, nowMinutes: number): number | null {
  if (day > today) return null;
  return day < today ? MINUTES_PER_DAY : nowMinutes;
}

// A deadline the venue's clock has already reached holds nothing. See §9.
export function isFutureHold(minutes: number, nowMinutes: number | null): boolean {
  return nowMinutes === null || minutes > nowMinutes;
}

// A new hold, priced on the time it would actually be held for. §9
export function holdChoices(
  perHourCents: readonly number[],
  config: HoldConfig,
  nowMinutes: number | null,
): HoldChoice[] {
  return holdSlots(config)
    .filter((minutes) => isFutureHold(minutes, nowMinutes))
    .map((minutes) => ({
      minutes,
      feeCents: holdFeeCents(perHourCents, minutes, config, nowMinutes),
    }));
}

// Priced by the gap it adds, so an extended hold is never paid for twice. §9
export function extensionChoices(
  perHourCents: readonly number[],
  config: HoldConfig,
  heldUntilMinutes: number,
  nowMinutes: number | null,
): HoldChoice[] {
  return holdSlots(config)
    .filter((minutes) => minutes > heldUntilMinutes && isFutureHold(minutes, nowMinutes))
    .map((minutes) => ({
      minutes,
      feeCents: holdExtensionCents(perHourCents, heldUntilMinutes, minutes),
    }));
}

export function canExtendHold(
  config: HoldConfig,
  heldUntilMinutes: number,
  nowMinutes: number | null,
): boolean {
  return extensionChoices([], config, heldUntilMinutes, nowMinutes).length > 0;
}

// A step off the grid prices a deadline nobody may pick; §9 allows neither.
export function isHoldMinutes(minutes: number, config: HoldConfig): boolean {
  return holdSlots(config).includes(minutes);
}

export function formatHoldMinutes(minutes: number): string {
  const hours = Math.floor(minutes / MINUTES_PER_HOUR);
  return `${hours}:${String(minutes % MINUTES_PER_HOUR).padStart(2, '0')}`;
}

// Inclusive of both ends: a one-day stay is one day, not zero. See §10.
export function stayDays(startsOn: string, endsOn: string): number {
  const from = Date.parse(`${startsOn}T00:00:00Z`);
  const to = Date.parse(`${endsOn}T00:00:00Z`);
  return Math.floor((to - from) / 86_400_000) + 1;
}

// A stay never ends before it starts: the To field follows the From. §10, §12
export function stayEnd(startsOn: string, endsOn: string): string {
  return endsOn < startsOn ? startsOn : endsOn;
}

export function stayTotalCents(
  dayPricesCents: readonly number[],
  days: number,
  feeCents: number,
): number {
  return dayPricesCents.reduce((total, price) => total + price, 0) * days + feeCents;
}
